import { parse as parseQuery } from 'query-string';

import { captureExceptionsToSentry, ResponseException } from '~/helpers/exceptionHandler';
import {
  convertToSnake, convertToCamel, MAX_FETCH_RETRIES, timeout,
  typhonAjaxFetch,
} from '~/helpers/api';
import { routes } from '~/helpers/routes';
import getCsrfToken from '~/helpers/csrf';
import { flashMessages } from '~/helpers/messages';

/**
 * @param {*} flags LaunchDarkly featureFlags in camelCase form, e.g. { sarReportV2: false }.
 * @param {*} location The window.location, used to detect local dev localhost and the query string.
 * How to use: append ?flags= to the URL then add the flag you want to override locally, e.g.
 * http://localhost:8000/?flags=flag1,flag2=false -> { flag1: true, flag2: false }.
 * The console.debug is visible as Verbose logging in Chrome. Inspired by react-launch-darkly.
 * flagStrings is an intermediate parsing of the flags, e.g. ['flag1', 'flag2=false'].
 * The query string parser treats keys with no values as null so we coalesce nullish (??) to true.
 * It also returns a string if there's a single param (e.g. ?foo) so we wrapflat to ensure an array.
 * flagOverrides has the same shape as flags, e.g. { flag1: true, flag2: false }.
 */
export function overrideFlagsViaQueryParams(flags, location) {
  if (/(localhost:800\d|(dev|staging|[a-z]+-qa)app.alloy.co)/.test(location.host)) {
    const flagStrings = [parseQuery(location.search, { arrayFormat: 'comma' }).flags || []].flat();
    const flagOverrides = flagStrings.reduce((overrides, flagStr) => ({
      ...overrides,
      [Object.keys(parseQuery(flagStr))[0]]:
        Object.values(parseQuery(flagStr, { parseBooleans: true }))[0] ?? true,
    }), {});
    Object.entries(flagOverrides).forEach(([key, value]) => {
      flags[key] = value;

      // Reduce the amount of console logs that appear in testing and CI
      if (process.env.TEST === 'true' || process.env.CI === 'true') {
        return;
      }
      console.debug(`Overriding feature flag ${key}: ${flags[key]}->${value} from query string.`); // eslint-disable-line no-console
    });
  }
}

export const getAjaxFetchOptions = (method, body, contentType) => {
  const options = {
    method,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRFToken': getCsrfToken(),
    },
  };
  if (contentType) {
    options.headers['Content-Type'] = contentType;
  }
  if (body) {
    options.body = contentType !== 'application/json'
      ? body : JSON.stringify(convertToSnake(body));
  }
  return options;
};

const handleAjaxFetchResponse = async (response, { method, url }) => {
  if (response.status === 200 || response.status === 201) {
    const data = await response.json();
    return {
      ...convertToCamel(data),
      // If response body contains permissions, keep them from being converted to camel
      // case to maintain backwards compatible with legacy front end permissions checks
      ...(data.permissions ? { permissions: data.permissions } : {}),
    };
  }

  if (response.status === 401) {
    return window.location.replace('/login');
  }
  if (response.status === 403) {
    const message = 'Data request forbidden. You don\'t have appropriate permissions.';
    if (window.addSnackBar) {
      window.addSnackBar({ title: message, type: 'danger' });
    } else {
      flashMessages([message]);
    }
  } else {
    const message = 'Something went wrong! Please try again or contact support.';
    if (window.addSnackBar) {
      window.addSnackBar({ title: message, type: 'danger' });
    } else {
      flashMessages([message]);
    }
  }
  throw new ResponseException(response, { method, url });
};

export const ajaxFetch = async (
  path,
  method,
  body = null,
  contentType = 'application/json',
  retries = MAX_FETCH_RETRIES,
) => {
  const url = `${window.location.origin}${path}`;
  const originatedUrlPath = window.location.pathname;

  try {
    const options = getAjaxFetchOptions(method, body, contentType);
    const response = await fetch(url, options);
    return handleAjaxFetchResponse(response, { method, url });
  } catch (error) {
    if (retries) {
      // Small back-off of few hundred ms that increases for subsequent retries
      await timeout(((MAX_FETCH_RETRIES - retries) * 100) + 100);
      return ajaxFetch(path, method, body, contentType, retries - 1);
    }

    captureExceptionsToSentry(error, {
      method: method || 'GET',
      url,
      originatedUrl: originatedUrlPath,
      retriesLeft: retries,
    });
    return Promise.reject(error);
  }
};

export const decimalToPercent = (num, decimal) => `${(num * 100).toFixed(decimal)}`;

export const isPositiveNumber = num => Math.sign(num) >= 0;

export const stringToNewLine = (str, maxLen) => {
  const strArr = str.split(' ');
  const result = [];
  let currStr = '';

  if (strArr.length === 1 && strArr[0].length > 14) {
    return str.match(/.{1,14}/g).join('\n');
  }
  strArr.forEach((word, i) => {
    const newStr = currStr.length > 0 ? `${currStr} ${word}` : word;
    if (newStr.length <= maxLen) {
      currStr = newStr;
      if (i === strArr.length - 1) {
        result.push(currStr.trim());
      }
    } else {
      result.push(`${currStr.trim()}\n`);
      currStr = word;
    }
  });
  return result.join(' ');
};

export const generateAudit = (
  typhonDomain,
  action,
  data,
) => {
  typhonAjaxFetch(`${typhonDomain}${routes.audit.auditActions}`, {
    method: 'POST',
    body: JSON.stringify({
      auditAction: {
        action,
        data,
      },
    }),
  });
};

export const getIsParentAgentAccessingChildCustomer = ({
  agent,
  customer,
}) => (
  agent.isAfefParentAgent
  && customer.isAfefChildOrganization
);
